import { Prose } from '../../components/Prose'
import { useSessions } from '../../api/hooks'
import { todayIso } from '../../lib/format'
import { addDays, weekStart } from '../../lib/schedule'
import { muscleTr, type CatalogExercise, type WorkoutDay } from '../../lib/program'
import {
  MUSCLE_ORDER, VOLUME, VOLUME_DETAY, VOLUME_LABEL, deloadCheck, plannedVolume, redistribute,
  returnAfterGap, sessionsInWeek, trainingPhase, volumeVerdict, weeklyVolume,
} from '../../lib/training'
import type { Profile } from '../../lib/metrics'
import type { ProgramEdits } from '../../lib/program'
import { HacimOnerileri } from './HacimOnerileri'

const n1 = (v: number) => (Math.round(v * 10) / 10).toLocaleString('tr-TR')

export function TrainingLoad({ catalog, profile, days, edits, equipment, onEkle }: {
  catalog: CatalogExercise[]; profile: Profile
  /** Güncel program — düzenlemeler dâhil. Plan hacmi buradan çıkıyor. */
  days: WorkoutDay[]
  /** Öneri motoru gün başına ekleme sınırını bilmek zorunda. */
  edits: ProgramEdits
  equipment: string[]
  /** Öneriyi uygula: programa hareket ekle. Kararı kullanıcı veriyor. */
  onEkle: (dayLabel: string, slug: string) => void
}) {
  const sessions = useSessions()
  const all = sessions.data ?? []
  const today = todayIso()

  const start = weekStart(today)
  const end = addDays(start, 6)
  const week = sessionsInWeek(all, start, end)
  const volume = weeklyVolume(week, catalog)
  const planned = plannedVolume(days, profile.strengthPerWeek || 3)

  const daysToBirth = profile.dueDate
    ? Math.round((Date.parse(profile.dueDate + 'T00:00:00') - Date.parse(today + 'T00:00:00')) / 86_400_000)
    : null
  const phase = trainingPhase(daysToBirth, profile.sessionMinutes)

  const plan = redistribute({
    targetWeekly: phase.weeklySets,
    days: profile.strengthPerWeek || phase.days,
    sessionMinutes: profile.sessionMinutes,
    musclesPerSession: 4,
    useSupersets: false,
  })

  const done = all.filter((s) => s.completedAt !== null)
  const lastDate = done[0]?.date
  const gapDays = lastDate
    ? Math.round((Date.parse(today + 'T00:00:00') - Date.parse(lastDate + 'T00:00:00')) / 86_400_000)
    : null
  const back = gapDays !== null && gapDays > 10 ? returnAfterGap(gapDays) : null

  const deload = deloadCheck(done, catalog)

  const rows = MUSCLE_ORDER
    .map((m) => ({ muscle: m, sets: volume[m] ?? 0, plan: planned[m] ?? 0 }))
    .filter((r) => r.sets > 0 || r.plan > 0
      || ['chest', 'lats', 'quadriceps', 'abdominals'].includes(r.muscle))

  if (sessions.isLoading) return null

  return (
    <div className="card">
      <h2>Haftalık yük</h2>
      <Prose className="card-h">
        {start.slice(8, 10)}.{start.slice(5, 7)} – {end.slice(8, 10)}.{end.slice(5, 7)} ·
        {' '}<strong>{phase.name}</strong> fazı · hedef {phase.weeklySets} set/kas
        {phase.loadPolicy === 'freeze' && ' · yük dondurulmuş'}
      </Prose>

      {back && (
        <div className="warnbox" style={{ marginTop: 0, marginBottom: 14 }}>
          <strong>Ara verilmiş:</strong> {back.message}
        </div>
      )}

      {deload.needed && (
        <div className="warnbox" style={{ marginTop: 0, marginBottom: 14, borderLeftColor: 'var(--s2)' }}>
          <strong>Deload zamanı.</strong> {deload.reasons.join(' ')}{' '}
          5–7 gün hacmi yarıya indir, yükü %10 azalt, sonra deload öncesi yüke dön.
          Takvime bağlı planlı deload yapmıyoruz — sadece veri gösterdiğinde.
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th>Kas</th>
            <th>Program</th>
            <th>Yapılan</th>
            <th style={{ width: '38%' }}>Programın hedefe göre yeri</th>
            <th>Durum</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const v = volumeVerdict(r.plan)
            return (
              <tr key={r.muscle}>
                <td>{muscleTr(r.muscle)}</td>
                <td>{n1(r.plan)}</td>
                <td style={{ color: r.sets === 0 ? 'var(--muted)' : 'inherit' }}>{n1(r.sets)}</td>
                <td>
                  <div className="bar-track" style={{ marginTop: 0 }}>
                    <div className="bar-fill" style={{
                      width: `${Math.min(100, (r.plan / VOLUME.CAP) * 100)}%`,
                      background: barColor(v),
                    }} />
                  </div>
                </td>
                <td style={{ color: v === 'below_mv' ? 'var(--muted)' : 'inherit' }}>
                  {/* title: masaüstünde üzerine gelince tam açıklama. Etiketin
                      kendisi de artık terim değil sonuç söylüyor. */}
                  <span title={VOLUME_DETAY[v]}>{VOLUME_LABEL[v]}</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <Prose className="note">
        <strong>Program</strong> sütunu, mevcut A/B günlerini haftada
        {' '}{profile.strengthPerWeek || 3} kez yaptığında bir kasa düşen set sayısı —
        programı düzenlediğinde anında değişir, bir hafta beklemene gerek yok.
        {' '}<strong>Yapılan</strong> ise bu hafta gerçekten işaretlediğin setler.
        {week.length === 0 && ' Bu hafta henüz tamamlanmış antrenman yok.'}
      </Prose>
      <Prose className="note">
        <strong>Durum</strong> sütunu haftalık set sayısının karşılığı:
        {' '}{VOLUME.MV} altı <strong>yetersiz</strong> (kas kaybını durdurmaya bile yetmiyor),
        {' '}{VOLUME.MV}–{VOLUME.MEV} <strong>korur ama büyütmez</strong>,
        {' '}{VOLUME.MEV}–{VOLUME.TARGET} <strong>büyütür</strong>,
        {' '}{VOLUME.TARGET}–{VOLUME.CAP} <strong>hedefte</strong>,
        {' '}{VOLUME.CAP} üstü <strong>fazla</strong> — fayda getirmiyor, toparlanmayı zorluyor.
      </Prose>
      <Prose className="note">
        Set sayısı <strong>fraksiyonel</strong>: birincil kas 1, ikincil 0.5 sayılıyor.
        Şınav göğse 1 set, tricepse ve ön omuza 0.5 yazıyor — bileşik hareket ağırlıklı
        bir programda toplam set sayısına bakmak hacmi olduğundan düşük gösterir.
      </Prose>

      <HacimOnerileri
        catalog={catalog} days={days} edits={edits} equipment={equipment}
        strengthPerWeek={profile.strengthPerWeek || 3}
        planned={planned} target={phase.weeklySets} onEkle={onEkle} />

      <h3>Gün sayısı ve süre</h3>
      <p className="sub" style={{ fontSize: 13.5 }}>{plan.note}</p>
      <Prose className="note">
        Gün sayısı düşünce haftalık hacmi azaltmak <strong>zorunlu değil</strong> — aynı seti
        daha az güne dağıtmak yeterli. Hacim sabitken frekansın kas büyümesine bağımsız
        katkısı pratikte sıfır. Asıl kısıt fizyoloji değil, takvim.
      </Prose>
    </div>
  )
}

function barColor(v: ReturnType<typeof volumeVerdict>): string {
  if (v === 'below_mv') return 'var(--grid)'
  if (v === 'maintenance') return 'var(--warn)'
  if (v === 'over_cap') return 'var(--s2)'
  return 'var(--s1)'
}
